import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { collection, DocumentData, getDocs, limit, orderBy, query, QueryDocumentSnapshot, startAfter } from 'firebase/firestore';
import { db } from '../config/firebase-config';
import { CompanyModel, UserProfile } from '../types/admin';

type AdminListState<T> = {
  items: T[];
  cursor: QueryDocumentSnapshot<DocumentData> | null;
  loading: boolean;
  error?: string | null;
}

type AdminState = {
  companies: AdminListState<CompanyModel>;
  users: AdminListState<UserProfile>;
}

const initialList = { items: [], cursor: null, loading: false, error: null };
const initialState: AdminState = { companies: { ...initialList }, users: { ...initialList } };

export const loadCompaniesInitial = createAsyncThunk('admin/loadCompaniesInitial', async (pageSize: number = 20) => {
  const q = query(collection(db, 'companies'), orderBy('createdAt'), limit(pageSize));
  const snap = await getDocs(q);
  const items = snap.docs.map(d => ({ id: d.id, ...d.data() })) as CompanyModel[];
  const cursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
  return { items, cursor } as { items: CompanyModel[]; cursor: QueryDocumentSnapshot<DocumentData> | null };
});

export const loadCompaniesNext = createAsyncThunk('admin/loadCompaniesNext', async ({ pageSize = 20, cursor }: { pageSize?: number; cursor: QueryDocumentSnapshot<DocumentData> }, { rejectWithValue }) => {
  try {
    const q = query(collection(db, 'companies'), orderBy('createdAt'), startAfter(cursor), limit(pageSize));
    const snap = await getDocs(q);
    const items = snap.docs.map(d => ({ id: d.id, ...d.data() })) as CompanyModel[];
    const nextCursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
    return { items, cursor: nextCursor } as { items: CompanyModel[]; cursor: QueryDocumentSnapshot<DocumentData> | null };
  } catch (e: any) {
    return rejectWithValue(e?.message || 'Failed to load next companies');
  }
});


export const loadUsersInitial = createAsyncThunk('admin/loadUsersInitial', async (pageSize: number = 20) => {
  const q = query(collection(db, 'users'), orderBy('email'), limit(pageSize));
  const snap = await getDocs(q);
  const items = snap.docs.map(d => ({ id: d.id, ...d.data() })) as UserProfile[];
  const cursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
  return { items, cursor } as { items: UserProfile[]; cursor: QueryDocumentSnapshot<DocumentData> | null };
});

export const loadUsersNext = createAsyncThunk('admin/loadUsersNext', async ({ pageSize = 20, cursor }: { pageSize?: number; cursor: QueryDocumentSnapshot<DocumentData> }, { rejectWithValue }) => {
  try {
    const q = query(collection(db, 'users'), orderBy('email'), startAfter(cursor), limit(pageSize));
    const snap = await getDocs(q);
    const items = snap.docs.map(d => ({ id: d.id, ...d.data() })) as UserProfile[];
    const nextCursor = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
    return { items, cursor: nextCursor } as { items: UserProfile[]; cursor: QueryDocumentSnapshot<DocumentData> | null };
  } catch (e: any) {
    return rejectWithValue(e?.message || 'Failed to load next users');
  }
});

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    resetCompanies(state) {
      state.companies = { ...initialList };
    },
    resetUsers(state) {
      state.users = { ...initialList };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadCompaniesInitial.pending, (state) => { state.companies.loading = true; state.companies.error = null; })
      .addCase(loadCompaniesInitial.fulfilled, (state, action: PayloadAction<{ items: CompanyModel[]; cursor: QueryDocumentSnapshot<DocumentData> | null }>) => {
        state.companies.loading = false;
        state.companies.items = action.payload.items;
        state.companies.cursor = action.payload.cursor;
      })
      .addCase(loadCompaniesInitial.rejected, (state, action) => { state.companies.loading = false; state.companies.error = action.error.message || null; })
      .addCase(loadCompaniesNext.pending, (state) => { state.companies.loading = true; state.companies.error = null; })
      .addCase(loadCompaniesNext.fulfilled, (state, action: PayloadAction<{ items: CompanyModel[]; cursor: QueryDocumentSnapshot<DocumentData> | null }>) => {
        state.companies.loading = false;
        state.companies.items = [...state.companies.items, ...action.payload.items];
        state.companies.cursor = action.payload.cursor;
      })
      .addCase(loadCompaniesNext.rejected, (state, action) => { state.companies.loading = false; state.companies.error = (action.payload as string) || action.error.message || null; })
      .addCase(loadUsersInitial.pending, (state) => { state.users.loading = true; state.users.error = null; })
      .addCase(loadUsersInitial.fulfilled, (state, action: PayloadAction<{ items: UserProfile[]; cursor: QueryDocumentSnapshot<DocumentData> | null }>) => {
        state.users.loading = false;
        state.users.items = action.payload.items;
        state.users.cursor = action.payload.cursor;
      })
      .addCase(loadUsersInitial.rejected, (state, action) => { state.users.loading = false; state.users.error = action.error.message || null; })
      .addCase(loadUsersNext.pending, (state) => { state.users.loading = true; state.users.error = null; })
      .addCase(loadUsersNext.fulfilled, (state, action: PayloadAction<{ items: UserProfile[]; cursor: QueryDocumentSnapshot<DocumentData> | null }>) => {
        state.users.loading = false;
        state.users.items = [...state.users.items, ...action.payload.items];
        state.users.cursor = action.payload.cursor;
      })
      .addCase(loadUsersNext.rejected, (state, action) => { state.users.loading = false; state.users.error = (action.payload as string) || action.error.message || null; });
  }
});

export const { resetCompanies, resetUsers } = adminSlice.actions;
export default adminSlice.reducer;
